import React, { useLayoutEffect, useRef, useState } from "react";
import { Badge, Button, Card, Col, Dropdown, Form, Row } from "react-bootstrap";
import {
  appendQuantityToken,
  compileQuantityConditional,
  parseQuantityConditional,
  QUANTITY_RELATIONS
} from "./quantityRuleUtils";

import { parseVector } from "./vectorRuleUtils";

const EMPTY_CONDITIONAL = {
  left: "0", relation: "==", right: '\"Igen\"', whenTrue: "1", whenFalse: "0"
};
const MODES = ["", "cur", "sum", "len", "idx", "idx0", "bef.sum", "aft.sum"];

function QuantityExpression({ value, onChange, activate, label, t, branches = true, vector = false }) {
  const [selectedToken, setSelectedToken] = useState(null);
  const inputRef = useRef(null);
  const insertRef = useRef(null);
  const pendingCaret = useRef(null);
  useLayoutEffect(() => {
    if (pendingCaret.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
  }, [value]);
  const rule = branches ? parseQuantityConditional(value) : null;
  const updateRule = (field, next) => onChange(compileQuantityConditional({ ...rule, [field]: next }));
  const selectToken = (event) => {
    const caret = event.currentTarget.selectionStart;
    const tokens = String(value).matchAll(/\(\*(\d+(?:@\d+)?)(?:\.(cur|sum|len|idx|idx0|bef\.sum|aft\.sum))?\*\)/g);
    setSelectedToken(null);
    for (const match of tokens) {
      if (caret >= match.index && caret <= match.index + match[0].length) {
        setSelectedToken({ start: match.index, text: match[0], id: match[1], mode: match[2] || "" });
        break;
      }
    }
  };
  insertRef.current = (token) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? start;
    pendingCaret.current = start + token.length;
    onChange(value.slice(0, start) + token + value.slice(end));
    setSelectedToken(null);
  };
  const focus = () => activate((token) => insertRef.current(token));
  const replaceExpression = (next) => {
    activate(null);
    setSelectedToken(null);
    onChange(next);
  };
  const clearExpression = () => replaceExpression(vector ? "[0,0,0]" : "0");

  if (rule) return (
    <div className="border rounded p-1 d-flex flex-wrap align-items-center gap-1" role="group" aria-label={label}>
      <Dropdown>
        <Dropdown.Toggle size="sm" variant="outline-danger" className="px-1 py-0"
          aria-label={`${label}: ${t("removeConditional")}`} title={t("removeConditional")}>×</Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => replaceExpression(rule.whenTrue)}>{t("keepTrueBranch")}</Dropdown.Item>
          <Dropdown.Item onClick={() => replaceExpression(rule.whenFalse)}>{t("keepFalseBranch")}</Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={clearExpression}>{t("clearExpression")}</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <span className="rounded bg-primary-subtle px-1 py-1 d-inline-flex flex-wrap align-items-center gap-1">
        <QuantityExpression value={rule.left} onChange={(next) => updateRule("left", next)} activate={activate} label={t("leftValue")} t={t} branches={false} />
        <Form.Select size="sm" value={rule.relation} aria-label={t("relation")}
          onChange={(event) => updateRule("relation", event.target.value)}
          className="border-0 shadow-none bg-transparent ps-1 pe-4" style={{ width: 62 }}>
          {QUANTITY_RELATIONS.map((relation) => <option key={relation} value={relation}>{relation}</option>)}
        </Form.Select>
        <QuantityExpression value={rule.right} onChange={(next) => updateRule("right", next)} activate={activate} label={t("rightValue")} t={t} branches={false} />
      </span>
      <span className="text-muted">?</span>
      <span className="rounded bg-success-subtle px-1 py-1 d-inline-flex flex-wrap align-items-center gap-1">
        <small className="fw-bold text-success">✓</small>
        <QuantityExpression value={rule.whenTrue} onChange={(next) => updateRule("whenTrue", next)} activate={activate} label={t("whenConditionTrue")} t={t} vector={vector} />
      </span>
      <span className="text-muted">:</span>
      <span className="rounded bg-danger-subtle px-1 py-1 d-inline-flex flex-wrap align-items-center gap-1">
        <small className="fw-bold text-danger">×</small>
        <QuantityExpression value={rule.whenFalse} onChange={(next) => updateRule("whenFalse", next)} activate={activate} label={t("whenConditionFalse")} t={t} vector={vector} />
      </span>
    </div>
  );

  const axes = vector ? parseVector(value) : null;
  if (axes) return (
    <div className="d-flex flex-column align-items-start gap-2">
      {axes.map((axisValue, index) => (
        <div key={index} className="d-flex flex-wrap align-items-center gap-2">
          <strong>{["X", "Y", "Z"][index]}</strong>
          <QuantityExpression value={axisValue} onChange={(next) => {
            const updated = [...axes];
            updated[index] = next;
            onChange(`[${updated.join(",")}]`);
          }} activate={activate} label={`${label} ${["X", "Y", "Z"][index]}`} t={t} />
        </div>
      ))}
      <Button size="sm" variant="outline-primary" aria-label={`${label}: ${t("addConditional")}`}
        onClick={() => { activate(null); onChange(compileQuantityConditional({ ...EMPTY_CONDITIONAL,
          whenTrue: `[${axes.join(",")}]`, whenFalse: "[0,0,0]" })); }}>
        {t("addConditional")}
      </Button>
      <Button size="sm" variant="outline-danger" onClick={clearExpression}
        aria-label={`${label}: ${t("clearExpression")}`}>{t("clearExpression")}</Button>
    </div>
  );

  const tokenIsCurrent = selectedToken && value.slice(selectedToken.start, selectedToken.start + selectedToken.text.length) === selectedToken.text;
  return (
    <span className="d-inline-flex flex-wrap align-items-center gap-1" style={{ maxWidth: "100%" }}>
      <Form.Control ref={inputRef} size="sm" value={value} title={label} aria-label={label}
        onFocus={focus} onClick={selectToken} onKeyUp={selectToken}
        onChange={(event) => { setSelectedToken(null); onChange(event.target.value); }}
        className="border-0 shadow-none bg-transparent px-1"
        style={{ width: `${Math.max(2, String(value).length) + 1}ch`, maxWidth: "100%", fontFamily: "monospace" }} />
      {tokenIsCurrent && <Form.Select size="sm" aria-label="Beállítás művelete" value={selectedToken.mode}
        style={{ width: "auto" }} onChange={(event) => {
          const mode = event.target.value;
          const token = `(*${selectedToken.id}${mode ? `.${mode}` : ""}*)`;
          onChange(value.slice(0, selectedToken.start) + token + value.slice(selectedToken.start + selectedToken.text.length));
          setSelectedToken({ ...selectedToken, text: token, mode });
        }}>
        {MODES.map((mode) => <option key={mode} value={mode}>{mode || "Alapérték"}</option>)}
      </Form.Select>}
      {tokenIsCurrent && <Button size="sm" variant="outline-danger" className="px-1 py-0"
        title={t("deleteToken")} aria-label={`${label}: ${t("deleteToken")}`}
        onClick={() => replaceExpression(value.slice(0, selectedToken.start) + value.slice(selectedToken.start + selectedToken.text.length))}>
        {t("deleteToken")}
      </Button>}
      {branches && <Button size="sm" variant="outline-primary" className="px-1 py-0"
        title={t("addConditional")} aria-label={`${label}: ${t("addConditional")}`}
        onClick={() => { activate(null); onChange(compileQuantityConditional({ ...EMPTY_CONDITIONAL, whenTrue: value || "1" })); }}>
        + ?
      </Button>}
      <Button size="sm" variant="outline-danger" className="px-1 py-0"
        title={t("clearExpression")} aria-label={`${label}: ${t("clearExpression")}`}
        onClick={clearExpression}>×</Button>
    </span>
  );
}

export default function QuantityRuleBuilder({ value, onChange, settings, t, label = t("quantity"), vector = false, hideTitle = false }) {
  const activeInsert = useRef(null);
  const activate = (insert) => { activeInsert.current = insert; };
  const insertToken = (token) => {
    if (activeInsert.current) activeInsert.current(token);
    else onChange(appendQuantityToken(value, token));
  };
  return (
    <Card className="mb-3 border-primary-subtle">
      <Card.Body>
        <Row className="g-3">
          <Col lg={8}>
            {!hideTitle && <Form.Label className="fw-semibold mb-2">{label}</Form.Label>}
            <QuantityExpression value={String(value ?? "")} onChange={onChange} activate={activate} label={label} t={t} vector={vector} />
          </Col>

          <Col lg={4}>
            <div className="rounded border p-3 h-100">
              <strong className="d-block mb-2">
                {t("insertableElements")}
              </strong>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {settings.map((setting) => (
                  <Button
                    key={setting.settingId}
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => insertToken(`(*${setting.settingId}*)`)}
                  >
                    {setting.name || `#${setting.settingId}`}
                  </Button>
                ))}
              </div>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {(vector ? ["width", "height", "depth", "qty"] : ["width", "height", "depth"]).map((dimension) => (
                  <Badge
                    key={dimension}
                    bg="light"
                    text="dark"
                    role="button"
                    tabIndex={0}
                    className="border p-2"
                    onClick={() => insertToken(dimension)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") insertToken(dimension);
                    }}
                  >
                    {t(dimension)}
                  </Badge>
                ))}
              </div>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {["+", "-", "*", "/", "%", "&&", "||", "!"].map((operator) => (
                  <Button
                    key={operator}
                    size="sm"
                    variant="outline-dark"
                    onClick={() => insertToken(operator)}
                  >
                    {operator}
                  </Button>
                ))}
              </div>
              <div className="d-flex flex-wrap gap-2">
                {["0", "1", "2", ...(vector ? ["3", "i(0,1)", ","] : []), '"Igen"', '"Nem"', "(", ")"].map((token) => (
                  <Button
                    key={token}
                    size="sm"
                    variant="outline-primary"
                    onClick={() => insertToken(token)}
                  >
                    {token}
                  </Button>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
